import React from 'react';
import uuid from 'uuid/v1';
import ArticleList from './article-list';
import { number } from 'prop-types';
import { booleanLiteral } from '@babel/types';

import { PAGE_NAME } from '../globals';

 /**
  * Collection of all available Articles
  * 
  * @interface
  * @property {Array<ArcticleInterface>} articles - all available articles
  * @property {ArticleInterface|null} selected - the article that has been selected or null if none
  * @example
  *
  * const MAL1 = {
  *   articles: [
  *     {
  *       id: '4',
  *       href: '#',
  *       imageSrc: '/place_holder/1.jpg',
  *       fullWidthImage: false,
  *       displayImage: true,
  *       title: 'Card Title',
  *       selected: false,
  *       featured: true,
  *       videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
  *       content: 'hello world',
  *     },
  *     {
  *       id: '5',
  *       href: '#',
  *       imageSrc: '/place_holder/2.jpg',
  *       fullWidthImage: false,
  *       displayImage: true,
  *       title: 'Card Title',
  *       selected: false,
  *       featured: true,
  *       videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
  *       content: 'hello world',
  *     },
  *     {
  *       id: '6',
  *       href: '#',
  *       imageSrc: '/place_holder/3.jpg',
  *       fullWidthImage: false,
  *       displayImage: true,
  *       title: 'Card Title',
  *       selected: false,
  *       featured: true,
  *       videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
  *       content: 'hello world',
  *     }
  *   ]
  * }
  * 
  * MAL1.selected = MAL1.articles[0];
  *
  */

export interface ArticlesInterface {
  articles: Array<ArticleInterface>,
  selected: ArticleInterface | null,
  [t:string]: any,
}

 /**
  * Contains the basic information needed to render an article on the featured article page
  * 
  * @interface
  * @property {string} id   - article id
  * @property {string} href - points to the article page
  * @property {string} imageSrc - the url of the article image
  * @property {string} title - the article title
  * @property {boolean} selected - true if the article is the article bein displayed
  * @property {boolean} featured - determines if the article is featured on the featured page
  * @example
  * 
  * const MA = {
  *   id: '4',
  *   href: '#',
  *   imageSrc: '/place_holder/1.jpg',
  *   title: 'Card Title',
  *   selected: false,
  *   featured: true,
  * };
  * 
  */
 
export interface MetaArticle {
  id: string,
  href: string,
  imageSrc: string,
  title: string,
  selected: boolean,
  featured: boolean,
  //[i:string]: any,
}

/**
 * Contains all information needed to render an article on the article page
 * 
 * @interface
 * @augments MetaArcticle
 * @property {string} videoUrl - URL of a video to embed on the article page
 * @property {string} content  - the body of the article
 * @property {boolean} fullWidthImage - true of feature image should display full width
 * @property {boolean} displayImage - true if image should be displayed in article view
 * 
 * @example
 * 
 * const MA = {
 *   id: '4',
 *   href: '#',
 *   imageSrc: '/place_holder/1.jpg',
 *   fullWidthImage: false,
 *   displayImage: true,
 *   title: 'Card Title',
 *   selected: false,
 *   featured: true,
 *   videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
 *   content: 'hello world this is the a test article',
 * };
 * 
 */
export interface ArticleInterface extends MetaArticle {
  videoUrl: string,
  fullWidthImage: boolean,
  displayImage: boolean,
  content: string,
}

/**
 * 
 * @class
 * @implements {ArticleInterface}
 * 

export class Article implements ArticleInterface {
  constructor(
    public id: string,
    public href: string,
    public imageSrc: string, 
    public title: string,
    public selected: boolean,
    public featured: boolean,
    public videoUrl: string,
    public content: string,
  ){ }
}
 */

/**
 * 
 * @class  
 * 
 */

export class Articles {
  private _selectedArticleId: string | undefined
  public updateArticle: <K extends keyof ArticleInterface>(
    id: string, 
    key: K,
    value: ArticleInterface[K],
  ) => ArticleInterface | null
  public generateId: () => string
  public createNewArticle: (
    title?: string,
    imageSrc?: string,
    fullWidthImage?: boolean,
    displayImage?: boolean,
    videoUrl?: string,
    content?: string,
    href?: string,
    id?: string,
    featured?: boolean,
  ) => ArticleInterface
  public deleteArticle: ( id: string) => void

  constructor(
    public articles: Array<ArticleInterface>,
  ){
    /**
     * produce a new Artile and prepends it to this.articles
     * @param {string} title - articles title
     * @param {string} imgSrc - image source
     * @param {string} videoUrl - video url
     * @param {content} content - articles content can contain html
     * @returns {ArticleInterface}
     */
    this.createNewArticle = (
      title?: string,
      imageSrc?: string,
      fullWidthImage?: boolean,
      displayImage?: boolean,
      videoUrl?: string,
      content?: string,
      href?: string,
      id?: string,
      featured?: boolean,
    ): ArticleInterface => {
      const newId = id || this.generateId();
      const newArticle = {
        id: newId,
        href: href || `/--${PAGE_NAME}#id=${newId}`,
        imageSrc: imageSrc || '',
        fullWidthImage: fullWidthImage || false,
        displayImage: displayImage || true,
        title: title || '',
        selected: false,
        featured: featured || true,
        videoUrl: videoUrl || '',
        content: content || '',
      };

      this.articles.unshift(newArticle);
      return newArticle;
    }

    /**
     * removes the article with the given id from the articleList
     * @param {string} id - id of the article we want to delete
     * 
     */
    this.deleteArticle = ( id: string): void => {
      const { articles } = this;
      const article = getArticleById(id, articles);
      if (article) {
        const articleIndex  = articles.indexOf(article);
        articles.splice(articleIndex, 1);
      }
    }

    /**
     * produces a new unique id 
     * @returns {number}
     * TODO
     */
    this.generateId = (): string => {
      return uuid();
    }
    /**
     * produce a new Article where the value of the attribute specified by
     * the given key is set to value. Also replaces the instance of the article
     * in this.articles with the new article.
     * 
     * @param {string} id - id of the article we are modifying
     * @param {string} key - the attribute on the article we would like to modify
     * @param {value} value - the value with would like to set it to
     * @returns {ArticleInterface | null} - the new article
     */
    this.updateArticle = <K extends keyof ArticleInterface>(
      id: string, 
      key: K,
      value: ArticleInterface[K],
    ): ArticleInterface | null => {
      const article = getArticleById(id, this.articles);
      if (article){
        const artcileIndex = this.articles.indexOf(article);
        const newArticle = Object.assign({}, article, {[key]: value});
        this.articles[artcileIndex] = newArticle;
        return newArticle;
      }

      return null;
    }
  }

  /**
   * returns the article associated with this.selectedArticleId
   */
  public get selectedArticle(): ArticleInterface | null {
    const { _selectedArticleId, articles } = this;
    if (_selectedArticleId) {
      return getArticleById(_selectedArticleId, articles);
    }
    return null;
  }

  /**
   * sets this.selectedArticleId to the given id if the article with that id
   * exists in this.articles. If it does not exist, leave this.selectedArticleId
   * unaltered.
   */

  public set selectedArticle(article: ArticleInterface | null) {
    if (article){
      const existingArticle = getArticleById(article.id, this.articles);
      if (existingArticle) {
        this._selectedArticleId = existingArticle.id;
      }
    } else {
      this._selectedArticleId = undefined;
    }
  }
}

/**
 * produce an Article from the fiven array of Articles that matches the given id or null if none
 * found.
 * 
 * @param {string} articleId - id of the article we are looking for
 * @param {Article[]} articleList - an array of articles to search
 * @returns {Article} - an Article with an "id" that matches the value of "articleId"
 * 
 * @example
 *    
 * const articleList: Array<MetaArticle> = [
 *   {
 *     id: '4',
 *     href: '#',
 *     imageSrc: '/place_holder/1.jpg',
 *     title: 'Card Title',
 *     selected: false,
 *     featured: true,
 *   },
 *   {
 *     id: '5',
 *     href: '#',
 *     imageSrc: '/place_holder/2.jpg',
 *     title: 'Card Title',
 *     selected: false,
 *     featured: true,
 *   },
 *   {
 *     id: '6',
 *     href: '#',
 *     imageSrc: '/place_holder/3.jpg',
 *     title: 'Card Title',
 *     selected: true,
 *     featured: true,
 *   }
 * ];
 * 
 * const article1 = getArticleById('6', articleList);
 * // should return:
 * // {
 * //   id: '6',
 * //   href: '#',
 * //   imageSrc: '/place_holder/3.jpg',
 * //   title: 'Card Title',
 * //   selected: true,
 * //   featured: true,
 * // }
 * 
 * const article2 = getArticleById('8', articleList);
 * // should return null;
 * 
 */

export function getArticleById(
  articleId: string, articleList: Array<ArticleInterface>
): ArticleInterface | null {
  for (let i = 0; i < articleList.length; i+=1) {
    let article = articleList[i];
    if (article.id === articleId) { return article}
  }

  return null;
}

/**
 * Selects the Article|MetaArticle with the given articleId and produces the article of the it
 * exists or null if it does not exist. To select an article we do the following:
 *   - If an article is currently selected, we unset the selected flag on that article
 *   - then we clear the selected field on the article list
 *   - and add the new article to the list
 * @param {string} articleId - the id of the article we would like to select
 * @param {MetaArticleList} articles - represents all the available articles.
 * @return {MetaArticle | null}  the selected Article or null if it does not exist
 * @example
 * 
 * const MAL1 = {
 *   articles: [
 *     {
 *       id: '4',
 *       href: '#',
 *       imageSrc: '/place_holder/1.jpg',
 *       title: 'Card Title',
 *       selected: true,
 *       featured: true,
 *     },
 *     {
 *       id: '5',
 *       href: '#',
 *       imageSrc: '/place_holder/2.jpg',
 *       title: 'Card Title',
 *       selected: false,
 *       featured: true,
 *     },
 *     {
 *       id: '6',
 *       href: '#',
 *       imageSrc: '/place_holder/3.jpg',
 *       title: 'Card Title',
 *       selected: false,
 *       featured: true,
 *     }
 *   ]
 * }
 * 
 * MAL1.selected = MAL1.articles[0];
 * 
 * selectArticleFromList('5', MAL1);
 * // should return:
 * //   {
 * //    id: '5',
 * //    href: '#',
 * //    imageSrc: '/place_holder/2.jpg',
 * //    title: 'Card Title',
 * //    selected: true,
 * //    featured: true,
 * //  }
 * //
 * // expect MAL1.articles[0].selected to be false
 * // expect MAL1.articles[1].selected to be true
 * //
 * 
 * selectArticleFromList('20', MAL1);
 * // should return null
 * // expect MAL1.articles[1].selected to be true
 * 
 *   - If an article is currently selected, we unset the selected flag on that article
 *   - then we clear the selected field on the article list
 *   - and add the new article to the list
 */

export function selectArticleFromList(
  articleId: string, articles: Articles
): ArticleInterface | null {
  const article = getArticleById(articleId, articles.articles);
  if (!article) { return null };

  const currentlySelected =  articles.selectedArticle;
  if (currentlySelected) {currentlySelected.selected = false};
  article.selected = true;
  articles.selectedArticle = article;

  return articles.selectedArticle;
}

/**
 * Deselect the currently selected article. To deselect an article we do the following:
 *   - If an article is currently selected, we set the selected flag on that article to false
 *   - then we clear the selected field on the article list
 * 
 * @param {Articles} articles - represents all the available articles.
 * @return {ArticleInterface | null}  the selected Article or null if it does not exist
 * @example
 *   
 * const MAL1 = {
 *   articles: [
 *     {
 *       id: '4',
 *       href: '#',
 *       imageSrc: '/place_holder/1.jpg',
 *       fullWidthImage: false,
 *       displayImage: true,
 *       title: 'Card Title',
 *       selected: true,
 *       featured: true,
 *       videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
 *       content: 'hello world',
 *     },
 *     {
 *       id: '5',
 *       href: '#',
 *       imageSrc: '/place_holder/2.jpg',
 *       fullWidthImage: false,
 *       displayImage: true,
 *       title: 'Card Title',
 *       selected: false,
 *       featured: true,
 *       videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
 *       content: 'hello world',
 *     },
 *     {
 *       id: '6',
 *       href: '#',
 *       imageSrc: '/place_holder/3.jpg',
 *       fullWidthImage: false,
 *       displayImage: true,
 *       title: 'Card Title',
 *       selected: false,
 *       featured: true,
 *       videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
 *       content: 'hello world',
 *     }
 *   ]
 * }
 * 
 * MAL1.selected = MAL1.articles[1];
 * 
 * deselectArticle(MAL1);
 * // should return:
 * //   {
 * //    id: '5',
 * //    href: '#',
 * //    imageSrc: '/place_holder/2.jpg',
 * //    fullWidthImage: false,
 * //    displayImage: true,
 * //    title: 'Card Title',
 * //    selected: true,
 * //    featured: true,
 * //    videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
 * //    content: 'hello world',
 * //  }
 * //
 * // expect MAL1.articles[1].selected to be false
 * //
 * 
 * selectArticleFromList(20, MAL1);
 * // should return null
 * // expect MAL1.articles[1].selected to be true
 */
export function deselectArticle(articles: Articles): ArticleInterface | null {
  const { selectedArticle } = articles;
  if (!selectedArticle){ return null }
  (selectedArticle as ArticleInterface).selected = false;
  articles.selectedArticle = null;

  return selectedArticle;
}

/**
 * Produce an array of Articles containing all Articles from a given array who's title start with
 * the given letter.
 * 
 * @author Adrian
 * @param {string} letter - the letter to filter by
 * @param {Array<ArticleInterface>} articles - represents all the available articles.
 * @return {Array<ArticleInterface>}  the selected Article or null if it does not exist
 * @example
 * 
 * const articles = [
 *     {
 *       id: '4',
 *       href: '#',
 *       imageSrc: '/place_holder/1.jpg',
 *       fullWidthImage: false,
 *       displayImage: true,
 *       title: 'A Card Title',
 *       selected: true,
 *       featured: true,
 *       videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
 *       content: 'hello world',
 *     },
 *     {
 *       id: '5',
 *       href: '#',
 *       imageSrc: '/place_holder/2.jpg',
 *       fullWidthImage: false,
 *       displayImage: true,
 *       title: 'B Card Title',
 *       selected: false,
 *       featured: true,
 *       videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
 *       content: 'hello world',
 *     },
 *     {
 *       id: '8',
 *       href: '#',
 *       imageSrc: '/place_holder/3.jpg',
 *       fullWidthImage: false,
 *       displayImage: true,
 *       title: 'Card Title',
 *       selected: false,
 *       featured: true,
 *       videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
 *       content: 'hello world',
 *     },
 *     {
 *       id: '6',
 *       href: '#',
 *       imageSrc: '/place_holder/3.jpg',
 *       fullWidthImage: false,
 *       displayImage: true,
 *       title: 'a Card Title',
 *       selected: false,
 *       featured: true,
 *       videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
 *       content: 'hello world',
 *     }
 *   ]
 * 
 * filterByTitleLetter('A', articles);
 *  // should return:
 *  // [
 *  //   {
 *  //     id: '4',
 *  //     href: '#',
 *  //     imageSrc: '/place_holder/1.jpg',
 *  //     fullWidthImage: false,
 *  //     displayImage: true,
 *  //     title: 'A Card Title',
 *  //     selected: true,
 *  //     featured: true,
 *  //     videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
 *  //     content: 'hello world',
 *  //   },
 *  //   {
 *  //     id: '6',
 *  //     href: '#',
 *  //     imageSrc: '/place_holder/3.jpg',
 *  //     fullWidthImage: false,
 *  //     displayImage: true,
 *  //     title: 'a Card Title',
 *  //     selected: false,
 *  //     featured: true,
 *  //     videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
 *  //     content: 'hello world',
 *  //   }
 *  // ]
 * 
 */
export function filterByTitleLetter(
  letter: string,
  articles: Array<ArticleInterface>
): Array<ArticleInterface> {
  const result = [];
  for (var i in articles) {
    let currentArticle = articles[i];
    let title = currentArticle.title.toLowerCase();

    if (title[0] == letter.toLowerCase()) {
      result.push(currentArticle);
    }
  }

  return result;
}

/**
 * Produce the given article with the given attribute set to the given value
 * 
 * @param {ArticleInterface} article - article to modify
 * @param {string} key - the Attribute to modify
 * @param {string} value - the new value to assign to the attribute
 * @param {function} transform - takes a string as argument, does some arbitrary transformation
 *                               on it and returns the transformed string
 * @return {ArticleInterface} the article with the modified videoUrl
 * @example
 * const myArticle = {
 *  id: '6',
 *  href: '#',
 *  imageSrc: '/place_holder/3.jpg',
 *  fullWidthImage: false,
 *  displayImage: true,
 *  title: 'a Card Title',
 *  selected: false,
 *  featured: true,
 *  videoUrl: 'https://www.youtube.com/embed/0-nufsLsoZo',
 *  content: 'hello world',
 * }
 * 
 * const result = updateArticleVideo(myArticle, 'videoUrl', 'https://www.youtube.com/embed/0-FAKE-VIDEO')
 * // expect result.videoUrl to be 'https://www.youtube.com/embed/0-FAKE-VIDEO'
 *     
 */
export function updateArticle<K extends keyof ArticleInterface>(
  article: ArticleInterface,
  key: K,
  value: ArticleInterface[K],
  transform?: (arg0: ArticleInterface[K]) => ArticleInterface[K],
): ArticleInterface {
  let newString = value;
  if (transform) { newString = transform(value) }
  article[key] = newString;
  return article;
}

/**
 * Produce JSX from a given string containing html
 * 
 * @property {string} html - html string to convert
 * @returns {React.ReactDOM} JSX form of given string
 */

export function renderContent(html: string): React.ReactElement {
  return <div dangerouslySetInnerHTML={{__html: html}}/>;
}

/**
 * Stores the JSON representation of the articles in the element corresponding
 * to the given selector
 * 
 * @param {Articles} articleList - the articleList it stage
 * @param {string} selector - points to a DOM element that holds the store
 *                          snippet
 * 
 */
export function stageArticleList(
  articleList: Array<ArticleInterface>, 
  selector: string
): void {
  const storeElement = document.querySelector(selector);
  if (storeElement) {
    storeElement.textContent = JSON.stringify(articleList);
  }
}

/**
 * Loads the JSON representation of the articles from the element referenced
 * by the given selector
 * 
 * @param {string} selector - references the DOM element that holds the data
 * @returns {Array<ArticleInterface> | null}
 */

export function loadArticleList(
  selector: string
): Array<ArticleInterface> | null {
  const storeElement: Element | null = document.querySelector(selector);
  if (storeElement) {
    const content = storeElement.textContent || '';
    const articleList = JSON.parse(content);
    return articleList;
  }
  return null;
}

/**
 * Produce True if The site CKEditor is anabled
 * 
 */

export function isEditingEnabled(): boolean {
  return (typeof CKEDITOR !== 'undefined');
}

/**
 * Produce an object containing:
 *   selectedLetter - selected letter or empty string
 *   selectedArticleId
 */

export function simpleRouter(urlHash: string): {selectedLetter: string | null, selectedArticleId: string | null} {
  function getSelectedArticleId(urlHash: string) {
    const reIdFilter = /(?=(id=(?<id>[0-9a-zA-Z-_]+)($|&)))/gi;
    const idMatch = reIdFilter.exec(urlHash);
    if (idMatch) {
      let result = (idMatch.groups as {id:string}).id;
      if (result) { return result }
    }
    return null;
  }

  function getFilterLetter(urlHash: string): string | null {
    const reFilterLetter  = /(?=(letterfilter=(?<letterFilter>[a-z])($|&)))/gi;
    const match = reFilterLetter.exec(urlHash);
    if (match) { return (match.groups as {letterFilter:string}).letterFilter }
    return null;
  }
  const selectedLetter = getFilterLetter(urlHash);
  const selectedArticleId = getSelectedArticleId(urlHash);

  return {selectedLetter, selectedArticleId}
}
